import { ref, readonly, onUnmounted, inject, type InjectionKey, type Ref } from 'vue';

/**
 * Provide a boolean ref under this key to force the input mode (used by stories
 * and tests). When provided, `useInputMode` returns it verbatim and skips the
 * `matchMedia` detection below.
 */
export const inputModeOverrideKey = Symbol('inputModeOverride') as InjectionKey<Ref<boolean>>;

const COARSE_POINTER_QUERY = '(pointer: coarse)';

function detectTouch(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(COARSE_POINTER_QUERY).matches;
}

/**
 * Reactive detection of touch vs. pointer-based input.
 *
 * `isTouch` is true on coarse-pointer devices (phones, tablets) where the round
 * screen renders the custom in-app keyboard, and false on fine-pointer devices
 * (desktop) where the native focused input + physical keyboard is used instead.
 * Detection is by `(pointer: coarse)` rather than viewport width so a small
 * desktop window keeps the physical keyboard and a large tablet still gets the
 * in-app keyboard.
 */
export function useInputMode(): { isTouch: Readonly<Ref<boolean>> } {
  const override = inject(inputModeOverrideKey, null);
  if (override) {
    return { isTouch: readonly(override) };
  }

  const isTouch = ref(detectTouch());

  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    const mql = window.matchMedia(COARSE_POINTER_QUERY);
    const update = (event: MediaQueryListEvent) => {
      isTouch.value = event.matches;
    };
    mql.addEventListener('change', update);
    onUnmounted(() => mql.removeEventListener('change', update));
  }

  return { isTouch: readonly(isTouch) };
}
